import { create } from 'zustand';
import type { UserRole } from '@shopgnd/shared';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  roles: UserRole[];
  avatarUrl?: string;
}

const AUTH_KEY = 'shopgnd-auth';
const persistMockAuth = false;

function loadUser(): AuthUser | null {
  if (!persistMockAuth) return null;
  try {
    const raw = sessionStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

function saveUser(user: AuthUser | null) {
  if (!persistMockAuth) return;
  if (user) sessionStorage.setItem(AUTH_KEY, JSON.stringify(user));
  else sessionStorage.removeItem(AUTH_KEY);
}

interface AuthState {
  user: AuthUser | null;
  /** False until the first Cognito session check on app boot completes. */
  authHydrated: boolean;
  setUser: (u: AuthUser | null) => void;
  setAuthHydrated: (hydrated: boolean) => void;
  signOut: () => void;
  hasRole: (r: UserRole) => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: loadUser(),
  authHydrated: persistMockAuth,
  setUser: (user) => {
    saveUser(user);
    set({ user });
  },
  setAuthHydrated: (authHydrated) => set({ authHydrated }),
  signOut: () => {
    saveUser(null);
    set({ user: null, authHydrated: true });
    void import('@/stores/adminImpersonationStore').then((m) => m.useAdminImpersonationStore.getState().clearSession());
    if (!persistMockAuth) {
      void import('@/lib/auth').then((m) => m.appSignOut());
    }
  },
  hasRole: (r) => {
    const u = get().user;
    return !!u?.roles.includes(r);
  },
}));
